import styled, { DefaultTheme } from 'styled-components'
import { BigNumber } from '@ethersproject/bignumber'
import { Box, Flex, FlexProps, Skeleton, Text } from 'uikit'
import { useTranslation } from 'react-i18next'
import { BetPosition, NodeRound, Round } from 'state/types'
import { useConfig } from 'pages/Predictions/context/ConfigProvider'
import { formatUsdv2, formatBnbv2, getRoundPosition, getPriceDifference } from '../../helpers'
import { formatMjt, formatUsd } from '../History/helpers'
import PositionTag from '../PositionTag'
import { CSSProperties } from 'react'

// PrizePoolRow
interface PrizePoolRowProps extends FlexProps {
  totalAmount: NodeRound['totalAmount']
}

const getPrizePoolAmount = (totalAmount: PrizePoolRowProps['totalAmount'], displayedDecimals: number) => {
  if (!totalAmount) {
    return '0'
  }

  return formatBnbv2(totalAmount, displayedDecimals)
}

const Row = ({ children, ...props }) => {
  return (
    <Flex alignItems="center" justifyContent="space-between" {...props}>
      {children}
    </Flex>
  )
}

export const PrizePoolRow: React.FC<PrizePoolRowProps> = ({ totalAmount, ...props }) => {
  const { t } = useTranslation()
  const { displayedDecimals } = useConfig()

  return (
    <Row {...props}>
      <Text bold color="textRemark" fontSize="14px" lineHeight="14px">
        {t('PREDICTION_37')}:
      </Text>
      <Text bold fontSize="14px" lineHeight="14px">{`${getPrizePoolAmount(totalAmount, displayedDecimals)} MJT`}</Text>
    </Row>
  )
}

// Payout Row
interface PayoutRowProps extends FlexProps {
  positionLabel: string
  multiplier: number
  amount: number
}

export const PayoutRow: React.FC<PayoutRowProps> = ({ positionLabel, multiplier, amount, ...props }) => {
  const formattedMultiplier = `${multiplier.toLocaleString(undefined, { maximumFractionDigits: 2 })}x`
  const { payToken } = useConfig()
  const { t } = useTranslation()

  return (
    <Row height="18px" {...props}>
      <Text fontSize="14px" color="textRemark">
        {positionLabel}:
      </Text>
      <Flex alignItems="center">
        <Text fontSize="14px" lineHeight="18px" color="textRemark">
          {/* {`${formattedMultiplier} Payout`} */}
          {t('PREDICTION_39', { amount: formattedMultiplier })}
        </Text>
        <Text fontSize="14px" color="textRemark" mx="10px">
          |
        </Text>
        <Text fontSize="14px" lineHeight="18px">{`${formatMjt(amount)} ${payToken.symbol}`}</Text>
      </Flex>
    </Row>
  )
}

interface LockPriceRowProps extends FlexProps {
  lockPrice: NodeRound['lockPrice']
}

export const LockPriceRow: React.FC<LockPriceRowProps> = ({ lockPrice, ...props }) => {
  const { t } = useTranslation()
  const { displayedDecimals, minPriceUsdDisplayed } = useConfig()
  return (
    <Row {...props}>
      <Text fontSize="14px" color="textRemark" lineHeight="14px">
        {t('PREDICTION_38')}:
      </Text>
      <Text fontSize="14px" lineHeight="14px">
        {formatUsdv2(lockPrice, minPriceUsdDisplayed, displayedDecimals)}
      </Text>
    </Row>
  )
}

// RoundResultBox
interface RoundResultBoxProps {
  betPosition?: BetPosition
  isNext?: boolean
  isLive?: boolean
  hasEntered?: boolean
  style?: CSSProperties
}

const getBackgroundColor = ({
  theme,
  betPosition,
  isNext,
  isLive,
  hasEntered,
}: RoundResultBoxProps & { theme: DefaultTheme }) => {
  if (isNext) {
    return 'rgba(115, 126, 141, 0.16)'
  }
  if (hasEntered || isLive) {
    if (betPosition === BetPosition.BEAR) return '#F9479C'
    return theme.colors.secondary
  }

  switch (betPosition) {
    case BetPosition.BULL:
      return theme.colors.success
    case BetPosition.BEAR:
      return '#F9479C'
    case BetPosition.HOUSE:
      return theme.colors.textDisabled
    default:
      return theme.colors.cardBorder
  }
}

const Background = styled(Box)<RoundResultBoxProps>`
  background: ${getBackgroundColor};
  border-radius: 14px;
  padding: 2px;
  width: 268px;
  margin-bottom: 1px;
`

const StyledRoundResultBox = styled.div`
  background: ${({ theme }) => theme.card.background};
  border-radius: 12px;
  padding: 16px;
`

export const RoundResultBox: React.FC<RoundResultBoxProps> = ({
  isNext = false,
  hasEntered = false,
  isLive = false,
  children,
  ...props
}) => {
  return (
    <Background isNext={isNext} hasEntered={hasEntered} isLive={isLive} {...props}>
      <StyledRoundResultBox>{children}</StyledRoundResultBox>
    </Background>
  )
}

interface RoundPriceProps {
  lockPrice: BigNumber
  closePrice: BigNumber
}

export const RoundPrice: React.FC<RoundPriceProps> = ({ lockPrice, closePrice }) => {
  const { displayedDecimals, minPriceUsdDisplayed } = useConfig()

  const betPosition = getRoundPosition(lockPrice, closePrice)
  const priceDifference = getPriceDifference(closePrice, lockPrice)

  const getTextColor = () => {
    switch (betPosition) {
      case BetPosition.BULL:
        return 'success'
      case BetPosition.BEAR:
        return '#F9479C'
      case BetPosition.HOUSE:
      default:
        return 'textDisabled'
    }
  }

  return (
    <Flex alignItems="center" justifyContent="space-between" mb="6px">
      {closePrice ? (
        <Text color={getTextColor()} bold fontSize="20px" style={{ minHeight: '24px' }}>
          {formatUsdv2(closePrice, minPriceUsdDisplayed, displayedDecimals)}
        </Text>
      ) : (
        <Skeleton height="24px" my="1px" />
      )}
      <PositionTag betPosition={betPosition}>
        {formatUsdv2(priceDifference, minPriceUsdDisplayed, displayedDecimals)}
      </PositionTag>
    </Flex>
  )
}

/**
 * TODO: Remove
 *
 * This is a temporary function until we consolidate the data coming from the graph versus the node
 */
interface PrizePoolHistoryRowProps extends FlexProps {
  totalAmount: number
}

const getPrizePoolAmountHistory = (totalAmount: PrizePoolHistoryRowProps['totalAmount']) => {
  if (!totalAmount) {
    return '0'
  }

  return formatMjt(totalAmount)
}

export const PrizePoolHistoryRow: React.FC<PrizePoolHistoryRowProps> = ({ totalAmount, ...props }) => {
  const { t } = useTranslation()

  return (
    <Row {...props}>
      <Text bold color="textRemark">
        {t('PREDICTION_37')}:
      </Text>
      <Text bold>{`${getPrizePoolAmountHistory(totalAmount)} MJT`}</Text>
    </Row>
  )
}

interface LockPriceHistoryRowProps extends FlexProps {
  lockPrice: Round['lockPrice']
}

export const LockPriceHistoryRow: React.FC<LockPriceHistoryRowProps> = ({ lockPrice, ...props }) => {
  const { t } = useTranslation()

  return (
    <Row {...props}>
      <Text fontSize="14px" color="textRemark">
        {t('PREDICTION_38')}:
      </Text>
      <Text fontSize="14px">{formatUsd(lockPrice)}</Text>
    </Row>
  )
}
/**
 * END TEMPORARY COMPONENTS
 */
